import type {
  CaseAssessment,
  CaseOutcome,
  DeliverySummary,
  ExecutionStatus,
  OutcomeResults,
  PresentedCaseStatus,
  PresentedResults,
  ResultStatus,
  Run,
  RunDetail,
  RunStatus,
  TestResult,
} from '@/lib/api/quality-types.js';

export const OUTCOME_LABEL: Record<CaseOutcome, string> = {
  unresolved: 'Requires review',
  passed: 'Passed',
  defect_found: 'Defect found',
  case_unclear: 'Test case issue',
  blocked_on_input: 'Blocked — access required',
  qa_error: 'Digital QA error',
  infra_error: 'Platform error',
  cancelled: 'Cancelled',
};

/**
 * Plain-language explanation for each outcome (R10) — every status shown on screen carries one,
 * so a reader outside the team never has to guess what the label means. `qa_error`'s tooltip says
 * outright that our own Digital QA is what failed, which is the whole point of that rename: a
 * customer must never read it as their application's fault.
 */
export const OUTCOME_TOOLTIP: Record<CaseOutcome, string> = {
  unresolved:
    'Nobody has confidently decided this case yet — an AI review could not reach a clear answer, or none has run. A person needs to look at the evidence.',
  passed:
    'The case ran end to end and everything it checked matched what was expected.',
  defect_found:
    'Digital QA ran the case correctly and the application would not let it through. This points to a real problem in the application for the team to investigate.',
  case_unclear:
    'The test case itself could not be carried out as written — a missing precondition, an unclear step, or a file that was never registered. Not a defect, and not a mistake by Digital QA.',
  blocked_on_input:
    'The case stopped because required access was unavailable, such as a missing or expired credential or an unanswered verification step. Resolve the access issue and restart the case.',
  qa_error:
    "Our own Digital QA made a mistake running this case — it skipped a check, misread the screen, or gave up too early. This is not the application's fault.",
  infra_error:
    "The run's own infrastructure failed before this case could produce a result, so nothing was actually tested. Not a defect, and not a mistake by Digital QA.",
  cancelled: 'This case was cancelled before it produced a result.',
};

export const EXECUTION_STATUS_LABEL: Record<ExecutionStatus, string> = {
  running: 'Running',
  awaiting_review: 'Awaiting review',
  awaiting_input: 'Needs input',
  reporting: 'Preparing report',
  completed: 'Completed',
  partially_completed: 'Partially completed',
  not_completed: 'Not completed',
  cancelled: 'Cancelled',
};

export const DELIVERY_STATE_LABEL: Record<DeliverySummary['state'], string> = {
  pending: 'Pending',
  partial: 'Partially delivered',
  delivered: 'Acknowledged',
  problem: 'Needs attention',
};

export const DEFAULT_RESTART_OUTCOMES = [
  'qa_error',
  'infra_error',
  'blocked_on_input',
] as const satisfies readonly CaseOutcome[];

export const OPTIONAL_RESTART_OUTCOMES = [
  'defect_found',
  'case_unclear',
] as const satisfies readonly CaseOutcome[];

const OUTCOMES = Object.keys(OUTCOME_LABEL) as CaseOutcome[];
const PRESENTED_KEYS = [...OUTCOMES, 'assessing'] as const;
const PRESENTED_STATUSES = new Set<string>([
  ...PRESENTED_KEYS,
  'queued',
  'running',
]);
const EXECUTION_STATUSES = new Set<string>(Object.keys(EXECUTION_STATUS_LABEL));
const RUN_STATUSES = new Set<string>([
  'running',
  'awaiting_review',
  'awaiting_input',
  'reporting',
  'passed',
  'failed',
  'incomplete',
  'cancelled',
]);
const RUN_KINDS = new Set<string>([
  'execution',
  'exploration',
  'doc_synthesis',
  'generation',
  'exploratory',
]);

/** Outcome counts as shown to people; `assessing` and `unresolved` appear only when non-zero. */
export type ShownResults = OutcomeResults & { assessing?: number };

export interface CanonicalOutcomeProjection {
  kind: 'canonical';
  executionStatus: ExecutionStatus;
  executionLabel: string;
  results: ShownResults;
  total: number;
  pending: number;
  executed: number;
  statusCaption: string | null;
  completionWarning: 'cleanup_unconfirmed' | null;
  reportRevision: number;
  provisional: boolean;
  deliverySummary: DeliverySummary | null;
  confirmedQaErrors: number;
  unconfirmedQaErrors: number;
}

export interface LegacyOutcomeProjection {
  kind: 'legacy';
  status: RunStatus;
  label: 'Legacy result';
}

export interface UnsupportedOutcomeProjection {
  kind: 'unsupported';
  message: string;
}

export type OutcomeProjection =
  | CanonicalOutcomeProjection
  | LegacyOutcomeProjection
  | UnsupportedOutcomeProjection;

function protocolVersion(run: { outcome_protocol_version?: unknown }): unknown {
  return run.outcome_protocol_version;
}

function canonicalResults(
  value: unknown,
  total: number,
): OutcomeResults | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const keys = Object.keys(row);
  const required = OUTCOMES.filter((outcome) => outcome !== 'unresolved');
  if (
    (keys.length !== required.length && keys.length !== OUTCOMES.length) ||
    keys.some((key) => !OUTCOMES.includes(key as CaseOutcome)) ||
    required.some(
      (outcome) => !Object.prototype.hasOwnProperty.call(row, outcome),
    )
  )
    return null;
  const results = {} as OutcomeResults & Record<string, number>;
  let sum = 0;
  for (const outcome of OUTCOMES) {
    const count = row[outcome];
    if (outcome === 'unresolved' && count === undefined) continue;
    if (typeof count !== 'number' || !Number.isInteger(count) || count < 0)
      return null;
    results[outcome] = count;
    sum += count;
  }
  if (sum > total) return null;
  return results;
}

/** The presented counts, or null when they are malformed or the 9 counts plus `pending` miss `total`. */
function validPresentedResults(
  value: unknown,
  pending: number,
  total: number,
): PresentedResults | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  if (Object.keys(row).length !== PRESENTED_KEYS.length) return null;
  let sum = 0;
  for (const key of PRESENTED_KEYS) {
    const count = row[key];
    if (typeof count !== 'number' || !Number.isInteger(count) || count < 0)
      return null;
    sum += count;
  }
  return sum + pending === total ? (row as PresentedResults) : null;
}

/** The presented counts derived from the cases, for a server that predates `presented_results`. */
function countPresentedResults(caseResults: TestResult[]): PresentedResults {
  const counts = Object.fromEntries(
    PRESENTED_KEYS.map((key) => [key, 0]),
  ) as PresentedResults;
  for (const result of caseResults) {
    const status = caseDisplayStatus(result);
    if (Object.prototype.hasOwnProperty.call(counts, status))
      counts[status as keyof PresentedResults] += 1;
  }
  return counts;
}

/** Drops the zero `unresolved`/`assessing` buckets, which older consumers never listed. */
function shownResults(presented: PresentedResults): ShownResults {
  const { unresolved, assessing, ...rest } = presented;
  return {
    ...rest,
    ...(unresolved > 0 ? { unresolved } : {}),
    ...(assessing > 0 ? { assessing } : {}),
  };
}

/**
 * Convert the API-cast run into a safe display union. Unknown protocol versions and malformed
 * canonical counters stay visible as unsupported data instead of falling through to legacy status.
 */
export function outcomeProjection(
  run: Run,
  summary?: RunDetail['summary'],
  caseResults?: TestResult[],
): OutcomeProjection {
  const version = protocolVersion(run);
  if (version !== 1) {
    if (version !== null) {
      return {
        kind: 'unsupported',
        message:
          version === undefined
            ? 'This run does not identify its outcome protocol.'
            : `Outcome protocol ${String(version)} is not supported by this app.`,
      };
    }
    if (typeof run.status !== 'string' || !RUN_STATUSES.has(run.status)) {
      return {
        kind: 'unsupported',
        message: 'This legacy run has no readable status.',
      };
    }
    return { kind: 'legacy', status: run.status, label: 'Legacy result' };
  }

  const runKind = (run as Run & { run_kind?: unknown }).run_kind;
  if (
    runKind !== null &&
    runKind !== undefined &&
    (typeof runKind !== 'string' || !RUN_KINDS.has(runKind))
  ) {
    return {
      kind: 'unsupported',
      message: `Run kind ${String(runKind)} is not supported by this app.`,
    };
  }
  const executionStatus = (run as Run & { execution_status?: unknown })
    .execution_status;
  const total = run.total;
  if (
    typeof executionStatus !== 'string' ||
    !EXECUTION_STATUSES.has(executionStatus) ||
    typeof total !== 'number' ||
    !Number.isInteger(total) ||
    total < 0
  ) {
    return {
      kind: 'unsupported',
      message: 'The canonical execution state is incomplete or invalid.',
    };
  }
  const rawResults =
    summary !== undefined &&
    Object.prototype.hasOwnProperty.call(summary, 'results')
      ? summary.results
      : run.results;
  const results = canonicalResults(rawResults, total);
  const reportRevision = run.report_revision;
  if (
    !results ||
    typeof reportRevision !== 'number' ||
    !Number.isInteger(reportRevision) ||
    reportRevision < 0 ||
    (summary !== undefined && summary.total !== total) ||
    (run.status_caption !== null &&
      run.status_caption !== undefined &&
      typeof run.status_caption !== 'string') ||
    (run.completion_warning !== null &&
      run.completion_warning !== undefined &&
      run.completion_warning !== 'cleanup_unconfirmed')
  ) {
    return {
      kind: 'unsupported',
      message: 'The canonical outcome counters or report revision are invalid.',
    };
  }
  const terminalCount = OUTCOMES.reduce(
    (sum, outcome) => sum + (results[outcome] ?? 0),
    0,
  );
  const businessExecution = (runKind ?? 'execution') === 'execution';
  if (!businessExecution && terminalCount !== 0) {
    return {
      kind: 'unsupported',
      message: 'A nonexecution run contains business-case outcome counters.',
    };
  }
  const pending = summary
    ? summary.pending
    : businessExecution
      ? Math.max(total - terminalCount, 0)
      : 0;
  if (
    typeof pending !== 'number' ||
    !Number.isInteger(pending) ||
    pending < 0 ||
    (businessExecution ? terminalCount + pending !== total : pending !== 0)
  ) {
    return {
      kind: 'unsupported',
      message: 'The canonical pending count does not match the run total.',
    };
  }
  const status = executionStatus as ExecutionStatus;
  if (isTerminalExecutionStatus(status) && reportRevision < 1) {
    return {
      kind: 'unsupported',
      message: 'The terminal canonical run has no finalized report revision.',
    };
  }
  const derivedUnconfirmed = (caseResults ?? []).filter(
    isUnconfirmedQaAssessment,
  ).length;
  const rawUnconfirmed =
    summary?.unconfirmed_qa_errors ??
    run.unconfirmed_qa_errors ??
    derivedUnconfirmed;
  if (
    !Number.isSafeInteger(rawUnconfirmed) ||
    rawUnconfirmed < 0 ||
    rawUnconfirmed > results.qa_error
  ) {
    return {
      kind: 'unsupported',
      message: 'The unconfirmed QA assessment count is invalid.',
    };
  }
  /** Presented counts fold unconfirmed QA errors into `unresolved`, so none are left to split out. */
  const presented = businessExecution
    ? (validPresentedResults(run.presented_results, pending, total) ??
      (caseResults?.length
        ? validPresentedResults(
            countPresentedResults(caseResults),
            pending,
            total,
          )
        : null))
    : null;
  return {
    kind: 'canonical',
    executionStatus: status,
    executionLabel: EXECUTION_STATUS_LABEL[status],
    results: presented ? shownResults(presented) : results,
    total,
    pending,
    executed: results.passed + results.defect_found + results.case_unclear,
    statusCaption: run.status_caption ?? null,
    completionWarning: run.completion_warning ?? null,
    reportRevision,
    provisional: !isTerminalExecutionStatus(status),
    deliverySummary: run.delivery_summary ?? null,
    confirmedQaErrors: presented
      ? presented.qa_error
      : results.qa_error - rawUnconfirmed,
    unconfirmedQaErrors: presented ? 0 : rawUnconfirmed,
  };
}

export function isUnconfirmedQaAssessment(
  result: Pick<TestResult, 'outcome' | 'needs_triage' | 'classification'>,
): boolean {
  return (
    result.outcome === 'qa_error' &&
    result.needs_triage === true &&
    result.classification?.source === 'fallback'
  );
}

/** A defect the AI judge did not confirm: it must never be shown as "Defect found". */
export function isUnconfirmedDefect(
  result: Pick<TestResult, 'outcome' | 'needs_triage'>,
): boolean {
  return result.outcome === 'defect_found' && result.needs_triage === true;
}

const JUDGE_ELIGIBLE_OUTCOMES = new Set<string>(['defect_found', 'qa_error']);

/**
 * A case the AI judge has not ruled on yet. QS keeps every case of a live run at revision 0 and
 * every case of a finished run above it, so revision 0 already means the run is not over.
 */
function isAssessing(
  result: Pick<TestResult, 'outcome' | 'outcome_revision' | 'classification'>,
): boolean {
  return (
    result.outcome_revision === 0 &&
    JUDGE_ELIGIBLE_OUTCOMES.has(
      String(result.classification?.preliminary_outcome ?? result.outcome),
    )
  );
}

type DisplayStatusInput = Pick<
  TestResult,
  | 'outcome'
  | 'outcome_revision'
  | 'needs_triage'
  | 'classification'
  | 'status'
  | 'display_status'
>;

/** True when a person must settle the case's outcome: the case shows as Requires review. */
export function requiresOutcomeReview(result: DisplayStatusInput): boolean {
  return caseDisplayStatus(result) === 'unresolved';
}

/**
 * The status a case shows: the server's `display_status` when it sends one. Without it, the same
 * rule locally: `assessing` before the judge rules, `unresolved` (Requires review) for an unresolved
 * case or an unconfirmed defect or QA error, else its canonical outcome, else the legacy status.
 */
export function caseDisplayStatus(
  result: DisplayStatusInput,
): PresentedCaseStatus | ResultStatus {
  if (
    typeof result.display_status === 'string' &&
    PRESENTED_STATUSES.has(result.display_status)
  )
    return result.display_status;
  if (isAssessing(result)) return 'assessing';
  if (
    result.outcome === 'unresolved' ||
    isUnconfirmedDefect(result) ||
    isUnconfirmedQaAssessment(result)
  )
    return 'unresolved';
  return typeof result.outcome === 'string' ? result.outcome : result.status;
}

const ASSESSING_LEAD =
  'An AI judge is checking this result. Steps and video are ready to view.';

/** Tooltip for an Assessing case: when the judge's answer is expected, or that it is late. */
export function assessingTooltip(
  assessment: CaseAssessment | null | undefined,
  now: number = Date.now(),
): string {
  if (assessment?.estimated === true)
    return `${ASSESSING_LEAD} Expected after the remaining cases finish.`;
  const readyBy =
    typeof assessment?.ready_by === 'string'
      ? new Date(assessment.ready_by)
      : null;
  if (!readyBy || Number.isNaN(readyBy.getTime())) return ASSESSING_LEAD;
  if (readyBy.getTime() <= now)
    return `${ASSESSING_LEAD} Taking longer than expected.`;
  const hours = String(readyBy.getHours()).padStart(2, '0');
  const minutes = String(readyBy.getMinutes()).padStart(2, '0');
  return `${ASSESSING_LEAD} Expected by ${hours}:${minutes}.`;
}

/** An applied AI reassessment is the only writer of this reason code, so it marks the rows whose
 *  audit trail must stay reachable even after the new outcome stops being reassessable. */
export function hasAppliedOutcomeReassessment(
  result: Pick<TestResult, 'classification'>,
): boolean {
  return result.classification?.reason_code === 'terminal_reassessment';
}

const REASSESSABLE_OUTCOMES = new Set<string>([
  'unresolved',
  'defect_found',
  'case_unclear',
  'qa_error',
]);

export function isOutcomeReassessmentCandidate(
  result: Pick<TestResult, 'outcome' | 'classification'>,
): boolean {
  return (
    result.outcome !== undefined &&
    REASSESSABLE_OUTCOMES.has(result.outcome) &&
    result.classification?.source !== 'deterministic'
  );
}

export function isTerminalExecutionStatus(status: ExecutionStatus): boolean {
  return (
    status === 'completed' ||
    status === 'partially_completed' ||
    status === 'not_completed' ||
    status === 'cancelled'
  );
}

export function runLifecycleStatus(
  run: Pick<Run, 'outcome_protocol_version' | 'execution_status' | 'status'>,
): ExecutionStatus | RunStatus | null {
  const version = protocolVersion(run);
  if (version === 1) {
    const status = (run as { execution_status?: unknown }).execution_status;
    return typeof status === 'string' && EXECUTION_STATUSES.has(status)
      ? (status as ExecutionStatus)
      : null;
  }
  if (version !== null) return null;
  const status = (run as { status?: unknown }).status;
  return typeof status === 'string' && RUN_STATUSES.has(status)
    ? (status as RunStatus)
    : null;
}

export function caseOutcome(result: TestResult): CaseOutcome | null {
  return result.outcome && OUTCOMES.includes(result.outcome as CaseOutcome)
    ? (result.outcome as CaseOutcome)
    : null;
}

export function restartableCases(detail: RunDetail): TestResult[] {
  const projection = outcomeProjection(detail.run, detail.summary);
  if (projection.kind === 'unsupported') return [];
  if (projection.kind === 'legacy') {
    return detail.results.filter(
      (result) =>
        result.status === 'failed' ||
        result.status === 'broken' ||
        result.status === 'timeout',
    );
  }
  if ((detail.run.run_kind ?? 'execution') !== 'execution') return [];
  return detail.results.filter((result) => {
    const outcome = caseOutcome(result);
    return (
      outcome !== null &&
      (DEFAULT_RESTART_OUTCOMES as readonly CaseOutcome[])
        .concat(OPTIONAL_RESTART_OUTCOMES)
        .includes(outcome)
    );
  });
}

export function correctionEligible(result: TestResult): boolean {
  const outcome = caseOutcome(result);
  return outcome !== null;
}

export function presentedOutcomeEntries(
  projection: CanonicalOutcomeProjection,
): [string, number][] {
  return Object.entries({
    ...projection.results,
    qa_error: projection.confirmedQaErrors,
    ...(projection.unconfirmedQaErrors > 0
      ? { unconfirmed_assessment: projection.unconfirmedQaErrors }
      : {}),
  });
}

export function outcomeLabel(outcome: CaseOutcome | 'assessing'): string {
  if (outcome === 'assessing') return 'Assessing';
  if (String(outcome) === 'unconfirmed_assessment')
    return 'Unconfirmed assessment';
  return Object.prototype.hasOwnProperty.call(OUTCOME_LABEL, outcome)
    ? OUTCOME_LABEL[outcome]
    : String(outcome);
}

/**
 * Tooltip text for the synthetic "unconfirmed assessment" tile — a `qa_error` verdict that predates
 * the reason-code column and cannot be told apart from a confirmed one by rule, so it is split out
 * and flagged rather than shown as either.
 */
const ASSESSING_CHIP_TOOLTIP =
  'An AI judge is still checking these results. Steps and video are ready to view.';

const UNCONFIRMED_ASSESSMENT_TOOLTIP =
  'An older QA-error verdict that predates the current classification rules, so it could not be confirmed against them. Review it directly.';

/**
 * Tooltip text for an outcome, honest about an unknown value: null for a status this map doesn't
 * cover (a newer server value this client predates, or a legacy `ResultStatus` sharing the same
 * badge) rather than a fabricated sentence. Own-property guarded like `outcomeLabel`, so a status
 * that happens to name an Object member can't resolve up the prototype chain.
 */
export function outcomeTooltip(outcome: string): string | null {
  if (outcome === 'unconfirmed_assessment')
    return UNCONFIRMED_ASSESSMENT_TOOLTIP;
  if (outcome === 'assessing') return ASSESSING_CHIP_TOOLTIP;
  return Object.prototype.hasOwnProperty.call(OUTCOME_TOOLTIP, outcome)
    ? OUTCOME_TOOLTIP[outcome as CaseOutcome]
    : null;
}

export function deliveryStateLabel(state: DeliverySummary['state']): string {
  return Object.prototype.hasOwnProperty.call(DELIVERY_STATE_LABEL, state)
    ? DELIVERY_STATE_LABEL[state]
    : String(state);
}
